/**
 * The dialogue answer (D235, actors round B2) — the player's words read as
 * an answer to an actor's parked question, and the TURN that answer makes.
 *
 * @remarks
 * The third kind of parked question (`conversation/prompts.ts`) is answered
 * where the other two are read: in `processPlayerWords`, after the
 * withdrawal and the chrome and before anything is parsed, because an
 * answer is not a command. Typed or chip-submitted, the words meet the
 * question's offered options whole (`dialogueAnswerOf` — never fuzzy); a
 * match comes here, and no match goes on to the parser with the question
 * still parked (D79's survival: a player may `look` mid-conversation and
 * answer next turn).
 *
 * What the answer IS: one executed turn with no command in it. The history
 * and the echo commit first, exactly as `runPlayerInput` commits them (D23:
 * every arm commits, so `(bundle, seed, inputs)` replays byte for byte);
 * the question is cleared; the option's lines and effects run as the
 * handled half of the turn (`answerPrompt`), and `completeTurn` gives it
 * the tail every turn gets — the tick, the watchers, the agents, the
 * commit — under the `dialogue_answered` occurrence, which carries the
 * prompt and the option for the triggers listening. A `leadsTo` has parked
 * the next question inside the turn; the survival rules see a fresh
 * question and keep it.
 *
 * The terminal gate is not read here: a closed world keeps no questions
 * (D225), so a dialogue question is never parked on one.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PlayerInputOutcome } from './player-input-outcome.js';

import { answerPrompt } from '../conversation/prompts.js';
import { completeTurn } from './complete-turn.js';
import { rememberConversationPartner } from './conversational-partner.js';
import { clearQuestion, type PendingDialogueQuestion } from './pending-question.js';
import { createTurnCursor, inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/**
 * Answer a parked dialogue question with one of its offered options.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input, the question parked on it
 * @param rawInput - Exactly what the player typed (a chip submits its label here)
 * @param question - The parked dialogue question
 * @param optionId - The offered answer the words matched
 * @returns The executed outcome — the turn the answer made
 */
export const answerDialogueQuestion = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	question: PendingDialogueQuestion,
	optionId: PendingDialogueQuestion['options'][number]['optionId'],
): PlayerInputOutcome => {
	// The verbatim history and the player echo, committed before anything can
	// run — the same opening `runPlayerInput` performs (D09/D23).
	const inputOrdinal = state.session.commandHistory.length + 1;
	const echo = stampNarrativeLines(
		bundle,
		state,
		inputNamespaceCursor(inputOrdinal, 0, state),
		[{ priority: 'standard', source: 'player', text: rawInput }],
		'player_input',
	);
	const openedState = clearQuestion({
		...state,
		session: {
			...state.session,
			commandHistory: [...state.session.commandHistory, rawInput],
			transcript: [...state.session.transcript, ...echo.entries],
		},
	});

	// The handled half, then the tail every turn gets.
	// Answering is addressing (D235): the asker is who the player is talking with.
	const answered = answerPrompt(bundle, rememberConversationPartner(openedState, question.actorId), createTurnCursor(openedState.progression.turnCount + 1), question, optionId);
	const result = completeTurn(bundle, openedState, answered, { detail: question.promptId, family: 'dialogue_answered', optionId });

	return {
		corrections: [],
		result: { ...result, transcriptEntries: [...echo.entries, ...result.transcriptEntries]},
		type: 'executed',
	};
};
